import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Pencil, Save, X } from 'lucide-react';
import { Button } from 'components/ui/button';
import type {
  ReferenceMatchResult,
  ReferenceMatchStatus,
} from 'contracts/HoraCertaImport';
import type { MatchOption } from '../../types';
import {
  isUnresolved,
  selectionKey,
  statusClassName,
  statusLabel,
} from '../../helpers';

interface HoraCertaMappingRowProps {
  match: ReferenceMatchResult;
  selectionValue?: number;
  isEditing: boolean;
  editingValue: string;
  isSaving: boolean;
  fieldError?: string;
  effectiveStatus: ReferenceMatchStatus;
  options: MatchOption[];
  onSelectionChange: (key: string, value: number | null) => void;
  onSearchReference: (
    entityType: ReferenceMatchResult['entityType'],
    search: string
  ) => Promise<void>;
  onEditingValueChange: (value: string) => void;
  onClearRowError: (key: string) => void;
  onStartEdit: (key: string, currentId?: number) => void;
  onCancelEdit: (key: string) => void;
  onSave: (match: ReferenceMatchResult, key: string) => void;
  registerFieldRef: (key: string, element: HTMLInputElement | null) => void;
}

export const HoraCertaMappingRow = ({
  match,
  selectionValue,
  isEditing,
  editingValue,
  isSaving,
  fieldError,
  effectiveStatus,
  options,
  onSelectionChange,
  onSearchReference,
  onEditingValueChange,
  onClearRowError,
  onStartEdit,
  onCancelEdit,
  onSave,
  registerFieldRef,
}: HoraCertaMappingRowProps) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [searchValue, setSearchValue] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState<{
    top: number;
    left: number;
    width: number;
    maxHeight: number;
  } | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [searchFailed, setSearchFailed] = useState(false);
  const key = selectionKey(match.entityType, match.normalizedValue);
  const resolved = !isUnresolved(match);
  const canEdit = match.status === 'mapped' && match.mappingId != null;
  const locked = resolved && !isEditing;
  const errorId = `${key.replace(/[^a-zA-Z0-9_-]/g, '-')}-error`;
  const selectedId = isEditing
    ? Number(editingValue)
    : locked
    ? match.resolved?.id
    : selectionValue;
  const selectedOption =
    options.find((option) => option.id === selectedId) ??
    (match.resolved?.id === selectedId ? match.resolved : undefined);
  const normalizedSearch = searchValue.trim();
  const visibleOptions = options.filter((option) =>
    option.text.toLocaleLowerCase().includes(normalizedSearch.toLocaleLowerCase())
  );
  const listId = `${key.replace(/[^a-zA-Z0-9_-]/g, '-')}-options`;

  useEffect(() => {
    if (!isOpen) {
      setMenuPosition(null);
      return;
    }

    const updateMenuPosition = () => {
      const input = inputRef.current;
      if (!input) return;

      const bounds = input.getBoundingClientRect();
      const margin = 8;
      const menuGap = 4;
      const maxMenuHeight = 208;
      const availableBelow = window.innerHeight - bounds.bottom - margin - menuGap;
      const availableAbove = bounds.top - margin - menuGap;
      const openAbove = availableBelow < 160 && availableAbove > availableBelow;
      const availableHeight = openAbove ? availableAbove : availableBelow;
      const maxHeight = Math.max(0, Math.min(maxMenuHeight, availableHeight));
      const left = Math.max(
        margin,
        Math.min(bounds.left, window.innerWidth - bounds.width - margin)
      );

      setMenuPosition({
        top: openAbove
          ? bounds.top - Math.min(maxMenuHeight, availableHeight) - menuGap
          : bounds.bottom + menuGap,
        left,
        width: Math.min(bounds.width, window.innerWidth - margin * 2),
        maxHeight,
      });
    };

    updateMenuPosition();
    window.addEventListener('resize', updateMenuPosition);
    window.addEventListener('scroll', updateMenuPosition, true);

    return () => {
      window.removeEventListener('resize', updateMenuPosition);
      window.removeEventListener('scroll', updateMenuPosition, true);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || normalizedSearch.length < 2) return;
    let active = true;
    const timer = window.setTimeout(() => {
      setIsSearching(true);
      setSearchFailed(false);
      void onSearchReference(match.entityType, normalizedSearch)
        .catch(() => {
          if (active) setSearchFailed(true);
        })
        .finally(() => {
          if (active) setIsSearching(false);
        });
    }, 250);

    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [isOpen, match.entityType, normalizedSearch, onSearchReference]);

  const selectOption = (option: MatchOption) => {
    if (isEditing) onEditingValueChange(String(option.id));
    else onSelectionChange(key, option.id);
    setSearchValue('');
    setIsOpen(false);
    onClearRowError(key);
  };

  const clearSelection = () => {
    if (isEditing) onEditingValueChange('');
    else onSelectionChange(key, null);
    setSearchValue('');
    setIsOpen(false);
    onClearRowError(key);
  };

  return (
    <tr className="hover:bg-slate-50/60">
      <td className="py-3 px-4 font-bold text-slate-800">
        {match.externalValue}
      </td>
      <td className="py-3 px-4">
        <div className="flex items-center gap-2">
          <div
            className="relative min-w-0 flex-1"
            onBlur={(event) => {
              if (!event.currentTarget.contains(event.relatedTarget as Node))
                setIsOpen(false);
            }}
          >
            <input
              ref={(element) => {
                inputRef.current = element;
                registerFieldRef(key, element);
              }}
              aria-label={`Correspondência para ${match.externalValue}`}
              aria-autocomplete="list"
              aria-controls={listId}
              aria-expanded={isOpen}
              aria-invalid={fieldError ? true : undefined}
              aria-describedby={fieldError ? errorId : undefined}
              autoComplete="off"
              className={`w-full text-xs font-sans text-slate-700 border rounded-md py-1.5 px-2 outline-none transition focus:border-[#EE7C2C] focus:ring-1 focus:ring-[#EE7C2C] ${
                fieldError
                  ? 'border-red-400 bg-red-50'
                  : 'border-slate-200 bg-white hover:bg-white focus:bg-white'
              }`}
              placeholder="Buscar no Webcol..."
              value={isOpen ? searchValue : selectedOption?.text ?? ''}
              disabled={locked}
              onFocus={() => {
                setSearchValue('');
                setIsOpen(true);
              }}
              onChange={(event) => {
                setSearchValue(event.target.value);
                setIsOpen(true);
              }}
              onKeyDown={(event) => {
                if (event.key === 'Escape') setIsOpen(false);
                if (event.key === 'Enter' && visibleOptions[0]) {
                  event.preventDefault();
                  selectOption(visibleOptions[0]);
                }
              }}
            />
            {isOpen &&
              menuPosition &&
              createPortal(
                <div
                  id={listId}
                  role="listbox"
                  className="overflow-y-auto rounded-md border border-slate-200 bg-white py-1 shadow-lg"
                  style={{
                    position: 'fixed',
                    top: menuPosition.top,
                    left: menuPosition.left,
                    width: menuPosition.width,
                    maxHeight: menuPosition.maxHeight,
                    zIndex: 1000,
                  }}
                >
                {selectedId != null && !locked && (
                  <button
                    type="button"
                    role="option"
                    aria-selected={false}
                    className="block w-full px-3 py-2 text-left text-xs text-slate-500 hover:bg-orange-50 focus:bg-orange-50 focus:outline-none"
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={clearSelection}
                  >
                    Limpar seleção
                  </button>
                )}
                {visibleOptions.map((option) => (
                  <button
                    key={option.id}
                    type="button"
                    role="option"
                    aria-selected={option.id === selectedId}
                    title={
                      option.document
                        ? `${option.documentType?.toUpperCase() ?? 'Documento'}: ${option.document}`
                        : undefined
                    }
                    className="group block w-full px-3 py-2 text-left text-xs text-slate-700 hover:bg-orange-50 focus:bg-orange-50 focus:outline-none"
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => selectOption(option)}
                  >
                    <span className="block">{option.text}</span>
                    {option.document && (
                      <span className="pointer-events-none mt-1 block text-[10px] font-normal text-slate-500">
                        {option.documentType?.toLowerCase() === 'cnpj'
                          ? 'CNPJ'
                          : 'Documento'}: {option.document}
                      </span>
                    )}
                  </button>
                ))}
                {isSearching && (
                  <p className="px-3 py-2 text-xs text-slate-500">Buscando...</p>
                )}
                {!isSearching && searchFailed && (
                  <p role="alert" className="px-3 py-2 text-xs text-red-600">
                    Não foi possível pesquisar no Webcol.
                  </p>
                )}
                {!isSearching && !searchFailed && visibleOptions.length === 0 && (
                  <p className="px-3 py-2 text-xs text-slate-500">
                    {normalizedSearch.length < 2
                      ? 'Digite ao menos 2 caracteres para pesquisar.'
                      : 'Nenhum cadastro encontrado.'}
                  </p>
                )}
                </div>,
                document.body
              )}
          </div>
          {canEdit && !isEditing && (
            <button
              type="button"
              aria-label={`Editar mapeamento de ${match.externalValue}`}
              title="Editar mapeamento"
              disabled={isSaving}
              className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#EE7C2C] disabled:opacity-50"
              onClick={() => onStartEdit(key, match.resolved?.id)}
            >
              <Pencil size={14} aria-hidden="true" />
            </button>
          )}
          {isEditing && (
            <>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                aria-label="Salvar mapeamento"
                title="Salvar mapeamento"
                disabled={!editingValue || isSaving}
                onClick={() => onSave(match, key)}
              >
                <Save size={16} aria-hidden="true" />
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                aria-label="Cancelar edição do mapeamento"
                title="Cancelar edição do mapeamento"
                disabled={isSaving}
                onClick={() => onCancelEdit(key)}
              >
                <X size={16} aria-hidden="true" />
              </Button>
            </>
          )}
        </div>
        {fieldError && (
          <p id={errorId} role="alert" className="mt-1 text-[11px] text-red-600">
            {fieldError}
          </p>
        )}
      </td>
      <td className="py-3 px-4">
        <span
          className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${statusClassName(
            effectiveStatus
          )}`}
        >
          {statusLabel(effectiveStatus)}
        </span>
      </td>
    </tr>
  );
};
